/**
 * Utility functions for clamping meta titles at 60 characters
 * Titles are built segment by segment, and segments are only added if they don't exceed 60 characters
 */

const MAX_TITLE_LENGTH = 60;

/**
 * Clamps a title to 60 characters maximum
 * If the title exceeds 60 characters, it truncates at the last word boundary before 60
 */
export function clampMetaTitle(title: string): string {
  if (title.length <= MAX_TITLE_LENGTH) {
    return title;
  }
  
  const truncated = title.substring(0, MAX_TITLE_LENGTH);
  const lastSpace = truncated.lastIndexOf(' ');
  
  // If we can find a space before 60 chars (preferably after 50), use it
  if (lastSpace > 50) {
    return truncated.substring(0, lastSpace).trim();
  }
  
  // Otherwise, just truncate at 60 and add ellipsis
  return truncated.trim() + '...';
}

/**
 * Builds a title by adding segments one at a time, stopping before exceeding MAX_TITLE_LENGTH.
 * The first segment (the job title) is always included in full and is never truncated —
 * only the appended suffix segments (company name, brand) are subject to the length limit.
 * @param segments - Array of segments (segments[0] is the primary title; rest are suffixes)
 * @param separator - String to join segments with (default: ' ')
 * @returns Complete first segment with as many suffixes as fit within MAX_TITLE_LENGTH
 */
export function buildClampedTitle(segments: string[], separator: string = ' '): string {
  if (segments.length === 0) {
    return '';
  }

  // Always use the full primary title; never truncate it
  let result = segments[0];

  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];
    const potentialTitle = result + separator + segment;

    // Only append the suffix if the combined length stays within the limit
    if (potentialTitle.length > MAX_TITLE_LENGTH) {
      break;
    }

    result = potentialTitle;
  }

  return result;
}

/**
 * Adds a segment to an existing title only if it doesn't exceed 60 characters
 * @param currentTitle - Current title string
 * @param segment - Segment to add
 * @param separator - String to join with (default: ' ')
 * @returns New title with segment added, or original title if adding would exceed limit
 */
export function addSegmentIfFits(currentTitle: string, segment: string, separator: string = ' '): string {
  const potentialTitle = currentTitle + separator + segment;
  
  if (potentialTitle.length <= MAX_TITLE_LENGTH) {
    return potentialTitle;
  }
  
  return currentTitle;
}
